import { formatDate } from "@/shared/lib/date-time";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EyeIcon } from "lucide-react";
import type { JamRegistrationListItemDTO } from "../api/client";
import {
  JAM_REGISTRATION_STATUS_LABELS,
  JAM_REGISTRATION_STATUS_VARIANTS,
} from "../lib/jam-status";

type JamRegistrationsTableProps = {
  isLoading: boolean;
  data: JamRegistrationListItemDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
  onView: (id: string) => void;
};

export function JamRegistrationsTable({
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onPaginationChange,
  onSortingChange,
  onView,
}: JamRegistrationsTableProps) {
  const columns: TanstackReactTable.ColumnDef<JamRegistrationListItemDTO>[] = [
    createSortableColumn<JamRegistrationListItemDTO>("submittedAt", "ວັນທີສະໝັກ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(row.original.submittedAt)}
        </span>
      ),
    }),
    createSortableColumn<JamRegistrationListItemDTO>("fullName", "ຜູ້ສະໝັກ", {
      size: 180,
      cell: ({ row }) => (
        <div>
          <p className="font-medium">
            {row.original.teamName ?? row.original.fullName}
          </p>
          {!row.original.teamName && row.original.nickname ? (
            <p className="text-muted-foreground text-xs">{row.original.nickname}</p>
          ) : null}
        </div>
      ),
    }),
    createSortableColumn<JamRegistrationListItemDTO>("phoneNumber", "ເບີໂທ", {
      size: 130,
      cell: ({ row }) => <span>{row.original.phoneNumber}</span>,
    }),
    {
      id: "category",
      header: "ໝວດໝູ່",
      size: 200,
      cell: ({ row }) => (
        <Badge variant="secondary" className="text-xs">
          {row.original.category?.name ?? "—"}
        </Badge>
      ),
    },
    createSortableColumn<JamRegistrationListItemDTO>("status", "ສະຖານະ", {
      size: 140,
      cell: ({ row }) => (
        <Badge
          variant={
            JAM_REGISTRATION_STATUS_VARIANTS[row.original.status] ?? "outline"
          }
        >
          {JAM_REGISTRATION_STATUS_LABELS[row.original.status] ??
            row.original.status}
        </Badge>
      ),
    }),
    {
      id: "actions",
      cell: ({ row }) => (
        <RowActions
          actions={[
            {
              label: "ເບິ່ງ",
              icon: <EyeIcon className="size-4" />,
              onClick: () => onView(row.original.id),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable<JamRegistrationListItemDTO, unknown>
      noDataMessage="ບໍ່ພົບໃບສະໝັກ Jam"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (!sorting || sorting.length === 0) return;
        if (sorting[0]?.id === "") return;
        onSortingChange(sorting[0]?.id ?? "", !!sorting[0]?.desc);
      }}
    />
  );
}
